// SAD-460 — Past Performance Reference fields of the Library create dialog,
// and the origin/provider/basis/outcome block of a reference's result card.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { BASIS_KINDS, labelOf, orUnknown, REFERENCE_ORIGINS } from './constants';
export function ReferenceFields({ form, onChange, providerMissing }) {
    const set = (patch)=>onChange({
            ...form,
            ...patch
        });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4 rounded-md border p-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-2 gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                children: "Reference origin"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: form.origin,
                                onValueChange: (v)=>set({
                                        origin: v
                                    }),
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.libraryPastPerformance.createOriginSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: REFERENCE_ORIGINS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                children: "Basis"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: form.basisKind,
                                onValueChange: (v)=>set({
                                        basisKind: v
                                    }),
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.libraryPastPerformance.createBasisSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: BASIS_KINDS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            providerMissing && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                className: "text-xs text-danger",
                children: "A partner reference needs a provider company — pick one above."
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(Label, {
                        htmlFor: "library-create-pp-provider-role",
                        children: "Provider role"
                    }),
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "library-create-pp-provider-role",
                        value: form.providerRole,
                        onChange: (e)=>set({
                                providerRole: e.target.value
                            }),
                        placeholder: "Prime contractor, subcontractor…",
                        "data-testid": Testids.libraryPastPerformance.createProviderRoleInput
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(Label, {
                        htmlFor: "library-create-pp-limitations",
                        children: "Limitations"
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        id: "library-create-pp-limitations",
                        value: form.limitations,
                        onChange: (e)=>set({
                                limitations: e.target.value
                            }),
                        placeholder: "What this reference does not establish",
                        "data-testid": Testids.libraryPastPerformance.createLimitationsInput
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-2 gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                htmlFor: "library-create-pp-outcome",
                                children: "Outcome (blank = unknown)"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "library-create-pp-outcome",
                                value: form.outcome,
                                onChange: (e)=>set({
                                        outcome: e.target.value
                                    }),
                                "data-testid": Testids.libraryPastPerformance.createOutcomeInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                htmlFor: "library-create-pp-contact",
                                children: "Reference contact (blank = unknown)"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "library-create-pp-contact",
                                value: form.referenceContact,
                                onChange: (e)=>set({
                                        referenceContact: e.target.value
                                    }),
                                "data-testid": Testids.libraryPastPerformance.createReferenceContactInput
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
export function ReferenceSummary({ details }) {
    return /*#__PURE__*/ _jsxs("dl", {
        className: "grid grid-cols-2 gap-x-3 gap-y-1 text-xs",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("dt", {
                        className: "text-muted-foreground",
                        children: "Origin"
                    }),
                    /*#__PURE__*/ _jsx("dd", {
                        "data-testid": Testids.libraryPastPerformance.resultOrigin,
                        children: labelOf(REFERENCE_ORIGINS, details.referenceOrigin)
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("dt", {
                        className: "text-muted-foreground",
                        children: "Provider role"
                    }),
                    /*#__PURE__*/ _jsx("dd", {
                        "data-testid": Testids.libraryPastPerformance.resultProviderRole,
                        children: orUnknown(details.providerRole)
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("dt", {
                        className: "text-muted-foreground",
                        children: "Basis"
                    }),
                    /*#__PURE__*/ _jsx("dd", {
                        "data-testid": Testids.libraryPastPerformance.resultBasis,
                        children: labelOf(BASIS_KINDS, details.basisKind)
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("dt", {
                        className: "text-muted-foreground",
                        children: "Outcome"
                    }),
                    /*#__PURE__*/ _jsx("dd", {
                        "data-testid": Testids.libraryPastPerformance.resultOutcome,
                        children: orUnknown(details.outcome)
                    })
                ]
            })
        ]
    });
}
